import { useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import LastReviewedBanner from "@/components/shared/LastReviewedBanner";
import { FRAMEWORKS_LIBRARY, FRAMEWORK_GROUPS, STANDARDS_WATCH, PORTFOLIO_LAST_REVIEWED } from "@/data/content";
import { FRAMEWORKS } from "@/constants/testIds";
import { Activity } from "lucide-react";

export default function FrameworksPage() {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all"
    ? FRAMEWORKS_LIBRARY
    : FRAMEWORKS_LIBRARY.filter((f) => f.group === filter);

  return (
    <>
      <PageHeader
        overline="Standards and Assurance"
        title="Standards-aware pathways for trustworthy resilience systems."
        lead="The global frameworks, standards, and initiatives that govern climate action, disaster risk reduction, and responsible technology — grouped, summarised, and linked back to the bodies that author them."
      />

      {/* Standards Watch */}
      <section className="ccdr-section bg-[#053043] text-white">
        <div className="ccdr-container">
          <div className="flex items-center gap-3 mb-5">
            <Activity className="w-5 h-5" style={{ color: "var(--ccdr-accent)" }} />
            <p
              className="text-xs uppercase tracking-[0.22em]"
              style={{ color: "var(--ccdr-accent)" }}
            >
              Standards Watch
            </p>
          </div>
          <h2 className="ccdr-h2 text-white mb-3">What the Centre is tracking now.</h2>
          <p className="text-[#CBD5E1] max-w-2xl mb-10">
            Active and emerging instruments across resilience and AI governance — refreshed against the authoritative
            sources, with status notes for leaders.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A6B8F]">
            {STANDARDS_WATCH.map((s) => (
              <article key={s.item} className="bg-[#053043] p-7 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="text-xs uppercase tracking-[0.22em] text-[#9CB0C2]"
                  >
                    {s.body_name}
                  </span>
                  <span
                    className="text-[10px] px-2 py-0.5 border border-[#D4A373] text-[#E9C46A]"
                    style={{ letterSpacing: "0.1em" }}
                  >
                    {s.status}
                  </span>
                </div>
                <h3 className="text-white text-lg mb-3" style={{ fontWeight: 500 }}>
                  {s.item}
                </h3>
                <p className="text-sm text-[#CBD5E1]">{s.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccdr-section">
        <div className="ccdr-container">
          <LastReviewedBanner date={PORTFOLIO_LAST_REVIEWED} source="Reference register · 28 sources" />
        </div>
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Filter sidebar */}
          <aside className="lg:col-span-3">
            <p className="ccdr-eyebrow mb-5">Filter</p>
            <div className="space-y-1">
              <button
                data-testid={FRAMEWORKS.filter("all")}
                onClick={() => setFilter("all")}
                className={`w-full text-left px-4 py-3 text-sm transition-colors border ${
                  filter === "all"
                    ? "bg-[#0B4F6C] text-white border-[#0B4F6C]"
                    : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#0B4F6C]"
                }`}
              >
                All ({FRAMEWORKS_LIBRARY.length})
              </button>
              {FRAMEWORK_GROUPS.map((g) => {
                const count = FRAMEWORKS_LIBRARY.filter((f) => f.group === g.slug).length;
                return (
                  <button
                    key={g.slug}
                    data-testid={FRAMEWORKS.filter(g.slug)}
                    onClick={() => setFilter(g.slug)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors border ${
                      filter === g.slug
                        ? "bg-[#0B4F6C] text-white border-[#0B4F6C]"
                        : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#0B4F6C]"
                    }`}
                  >
                    {g.label} ({count})
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="lg:col-span-9">
            {filter === "all"
              ? FRAMEWORK_GROUPS.map((g) => (
                  <FrameworkGroup
                    key={g.slug}
                    label={g.label}
                    items={FRAMEWORKS_LIBRARY.filter((f) => f.group === g.slug)}
                  />
                ))
              : (
                <FrameworkGroup
                  label={FRAMEWORK_GROUPS.find((g) => g.slug === filter).label}
                  items={filtered}
                />
              )}
          </div>
        </div>
      </section>

      <CTASection
        title="Filter the library by theme, or read how these instruments connect in the focus areas."
        primary={{ to: "/focus-areas", label: "See the focus areas" }}
      />
    </>
  );
}

function FrameworkGroup({ label, items }) {
  return (
    <div className="mb-14">
      <h2 className="ccdr-h2 mb-8">{label}</h2>
      <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
        {items.map((f) => (
          <details
            key={f.slug}
            data-testid={FRAMEWORKS.row(f.slug)}
            className="group p-6 lg:p-8 hover:bg-[#FAFAFA] transition-colors"
          >
            <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
              <div className="flex-1">
                <h3 className="ccdr-h3 mb-2">{f.name}</h3>
                <p className="text-sm text-[#4B5563]">{f.body_short}</p>
              </div>
              <span
                className="text-xs uppercase tracking-[0.22em] text-[#B07D4B] flex-shrink-0 mt-1"
              >
                <span className="group-open:hidden">Expand</span>
                <span className="hidden group-open:inline">Collapse</span>
              </span>
            </summary>
            <div className="mt-5 ccdr-prose max-w-3xl">
              <p>{f.body}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
